import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  ChevronUp,
  ChevronDown,
  DollarSign,
  Link2,
  Settings2,
  Users,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { RoomCombination } from "../hooks/useSearchAndRank";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CONTRACT_TERM_OPTIONS,
  DEFAULT_CONTRACT_TERM,
  DEFAULT_LICENSE_FEE_MODE,
  DEFAULT_LICENSE_FEE_PERCENTAGE,
  DEFAULT_PROPOSAL_TYPE,
  PROPOSAL_TYPE_OPTIONS,
  type AdjustmentsData,
  type AIModeState,
  type SpaceAdjustment,
  type LicenseFeeMode,
  type ProposalType,
  type SpaceStaleStatus,
} from "../types";
import {
  buildSpaceAdjustment,
  calculateSpaceValues,
  getMonthlyPrice,
} from "../utils/spaceAdjustments";
import {
  DEFAULT_SPACE_TAKE_PCT,
  FIXED_CREDIT_PRICE,
} from "../../pricing/marginCalculator";
import { calculateLicenseFeeFromAdjustments } from "../utils/licenseFee";
import type { RankedSpace } from "../types";
import { Divider } from "../components/Divider";
import { SpaceAdjustmentCard } from "../components/SpaceAdjustmentCard";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";

// Labels are display-only; the ProposalType value is what the backend receives.
const PROPOSAL_TYPE_LABELS: Record<ProposalType, string> = {
  ENT: "Enterprise",
  SMB: "SMB",
};

interface AdjustmentsStepProps {
  data: AdjustmentsData | null;
  state: AIModeState;
  onNext: (data: AdjustmentsData) => void;
  onChange?: (data: AdjustmentsData) => void;
  onValidityChange?: (valid: boolean) => void;
  registerNext?: (fn: () => void) => void;
  originalSpaces?: RankedSpace[];
  isEditMode?: boolean;
}

export default function AdjustmentsStep({
  data,
  state,
  onNext,
  onChange,
  onValidityChange,
  registerNext,
  originalSpaces,
  isEditMode,
}: AdjustmentsStepProps) {
  const { userInfo } = useAuth();
  const rankedSpaces = state.location?.rankedSpaces;
  const locationExtraSpaces = state.location?.extraSpaces ?? [];
  const selectedSpaceIds = state.location?.selectedSpaceIds;
  // Stable references so the memos below don't recompute on every render.
  const selectedComboIds = useMemo(
    () => state.location?.selectedComboIds ?? [],
    [state.location?.selectedComboIds],
  );
  const roomCombinations = useMemo(
    () => state.location?.roomCombinations ?? [],
    [state.location?.roomCombinations],
  );

  // Rooms in the proposal = individually-selected ∪ rooms of the selected combos.
  const proposalRoomIds = useMemo(() => {
    const ids = new Set(selectedSpaceIds ?? []);
    for (const combo of roomCombinations) {
      if (!selectedComboIds.includes(combo.combinationId)) continue;
      for (const r of combo.rooms) ids.add(r.privateRoomId);
    }
    return ids;
  }, [selectedSpaceIds, selectedComboIds, roomCombinations]);

  const selectedSpaces = useMemo(
    () =>
      [...(rankedSpaces ?? []), ...locationExtraSpaces].filter((s) =>
        proposalRoomIds.has(s.id),
      ),
    [rankedSpaces, locationExtraSpaces, proposalRoomIds],
  );

  const staleStatusBySpaceId = useMemo(() => {
    if (!originalSpaces?.length) return new Map<string, SpaceStaleStatus>();
    const originalById = new Map(originalSpaces.map((s) => [s.id, s]));
    return new Map(
      selectedSpaces.flatMap((space) => {
        const original = originalById.get(space.id);
        if (!original) return [];
        const changed =
          space.pricePerWorkstation !== original.pricePerWorkstation ||
          space.monthlyPrice !== original.monthlyPrice ||
          space.capacity !== original.capacity;
        if (!changed) return [];
        return [[space.id, "data-changed" as SpaceStaleStatus]];
      }),
    );
  }, [originalSpaces, selectedSpaces]);

  const [orderedSpaceIds, setOrderedSpaceIds] = useState<string[]>(() => {
    const saved = data?.orderedSpaceIds;
    if (saved?.length) {
      // preserve saved order, appending any new spaces at the end
      const savedSet = new Set(saved);
      const extras = selectedSpaces
        .map((s) => s.id)
        .filter((id) => !savedSet.has(id));
      return [
        ...saved.filter((id) => selectedSpaces.some((s) => s.id === id)),
        ...extras,
      ];
    }
    return selectedSpaces.map((s) => s.id);
  });

  const orderedSpaces = useMemo(
    () =>
      orderedSpaceIds
        .map((id) => selectedSpaces.find((s) => s.id === id))
        .filter((s): s is RankedSpace => s != null),
    [orderedSpaceIds, selectedSpaces],
  );

  // Combos explicitly selected by the executive (a first-class selection, not
  // "all rooms happen to be selected").
  const combinationGroups = useMemo(() => {
    return roomCombinations
      .filter((c) => selectedComboIds.includes(c.combinationId))
      .map((c) => ({
        combinationId: c.combinationId,
        roomIds: c.rooms.map((r) => r.privateRoomId),
        combo: c,
      }));
  }, [roomCombinations, selectedComboIds]);

  type AdjustmentItem =
    | { type: "single"; spaceId: string }
    | {
        type: "combo";
        combinationId: string;
        spaceIds: string[];
        combo: RoomCombination;
      };

  const orderedItems = useMemo<AdjustmentItem[]>(() => {
    const comboRoomIds = new Set(combinationGroups.flatMap((g) => g.roomIds));
    const individualIds = new Set(
      selectedSpaces.filter((s) => s.isIndividual).map((s) => s.id),
    );
    const emittedCombos = new Set<string>();
    const items: AdjustmentItem[] = [];
    const pushCombo = (group: (typeof combinationGroups)[number]) => {
      if (emittedCombos.has(group.combinationId)) return;
      emittedCombos.add(group.combinationId);
      items.push({
        type: "combo",
        combinationId: group.combinationId,
        spaceIds: group.roomIds,
        combo: group.combo,
      });
    };
    for (const id of orderedSpaceIds) {
      // A room shows as a standalone card when it was picked individually OR it
      // belongs to no combo — so an office that's both individual AND in a combo
      // appears both here and inside its combo card (mirrors the proposal view).
      if (individualIds.has(id) || !comboRoomIds.has(id)) {
        items.push({ type: "single", spaceId: id });
      }
      // Emit each combo this room belongs to once, anchored at its first room.
      for (const group of combinationGroups) {
        if (group.roomIds.includes(id)) pushCombo(group);
      }
    }
    // Safety net: a selected combo whose rooms never appear in the individual
    // ordering (e.g. combo-only rooms not present in the ranked/extra pool) still
    // renders. Without this the combo would silently drop from the Ajustes step.
    for (const group of combinationGroups) pushCombo(group);
    return items;
  }, [orderedSpaceIds, combinationGroups, selectedSpaces]);

  const [proposalType, setProposalType] = useState<ProposalType>(
    data?.proposalType ?? DEFAULT_PROPOSAL_TYPE,
  );
  const [contractMonths, setContractMonths] = useState<
    AdjustmentsData["contractMonths"]
  >(data !== null ? data.contractMonths : DEFAULT_CONTRACT_TERM);
  const [spaceAdjustments, setSpaceAdjustments] = useState<SpaceAdjustment[]>(
    () => {
      const savedMap = new Map(
        (data?.spaceAdjustments ?? []).map((a) => [a.spaceId, a]),
      );
      return selectedSpaces.map((space) =>
        buildSpaceAdjustment(space, savedMap.get(space.id)),
      );
    },
  );
  const [proposalValidity, setProposalValidity] = useState(
    data?.proposalValidity
      ? new Date(data.proposalValidity).toISOString().split("T")[0]
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
  );
  const [sellerNotes, setSellerNotes] = useState(data?.sellerNotes ?? "");
  const [showLicenseFee, setShowLicenseFee] = useState(
    data?.showLicenseFee ?? true,
  );
  const [licenseFeeMode, setLicenseFeeMode] = useState<LicenseFeeMode>(
    data?.licenseFeeMode ?? DEFAULT_LICENSE_FEE_MODE,
  );
  const [licenseFeePercentage, setLicenseFeePercentage] = useState(
    data?.licenseFeePercentage ?? DEFAULT_LICENSE_FEE_PERCENTAGE,
  );
  const [licenseFeeFixed, setLicenseFeeFixed] = useState<number | undefined>(
    data?.licenseFeeFixed,
  );

  const licenseFeeCalc = useMemo(
    () =>
      calculateLicenseFeeFromAdjustments(
        licenseFeeMode,
        licenseFeePercentage,
        licenseFeeFixed,
        spaceAdjustments,
        contractMonths,
      ),
    [
      licenseFeeMode,
      licenseFeePercentage,
      licenseFeeFixed,
      spaceAdjustments,
      contractMonths,
    ],
  );
  const [includeSetupFee, setIncludeSetupFee] = useState(
    data?.includeSetupFee ?? false,
  );
  const [setupFee, setSetupFee] = useState<number | "">(data?.setupFee ?? "");
  const [includeFlexCredits, setIncludeFlexCredits] = useState(
    data?.includeFlexCredits ?? false,
  );
  const [flexCredits, setFlexCredits] = useState<number | "">(
    data?.flexCredits ?? "",
  );
  const [recommendedSpaceIds, setRecommendedSpaceIds] = useState<string[]>(
    data?.recommendedSpaceIds ?? [],
  );

  const handleToggleRecommended = useCallback((spaceId: string) => {
    setRecommendedSpaceIds((prev) =>
      prev.includes(spaceId)
        ? prev.filter((id) => id !== spaceId)
        : [...prev, spaceId],
    );
  }, []);

  const handleToggleComboRecommended = useCallback((roomIds: string[]) => {
    setRecommendedSpaceIds((prev) => {
      const allOn = roomIds.every((id) => prev.includes(id));
      return allOn
        ? prev.filter((id) => !roomIds.includes(id))
        : [...prev.filter((id) => !roomIds.includes(id)), ...roomIds];
    });
  }, []);

  const handleProposalTypeChange = useCallback(
    (type: ProposalType) => {
      setProposalType(type);
      sendTrackDataServer({
        eventName: "PROPOSAL_TYPE_SELECTED",
        productType: "PO",
        url: window.location.pathname,
        data: {
          deal_id: state.diagnosis?.dealId ?? null,
          user_id: userInfo?.user?.id ?? null,
          email: userInfo?.profile?.email ?? null,
          proposal_type: type,
        },
      });
    },
    [state.diagnosis?.dealId, userInfo],
  );

  const handleLicenseFeeModeChange = useCallback(
    (mode: LicenseFeeMode) => {
      setLicenseFeeMode(mode);
      sendTrackDataServer({
        eventName: "PROPOSAL_LICENSE_FEE_FORMAT_SELECTED",
        productType: "PO",
        url: window.location.pathname,
        data: {
          deal_id: state.diagnosis?.dealId ?? null,
          user_id: userInfo?.user?.id ?? null,
          email: userInfo?.profile?.email ?? null,
          format: mode,
        },
      });
    },
    [state.diagnosis?.dealId, userInfo],
  );

  const handleExtraValueChange = useCallback(
    (spaceId: string, extraValue: number) => {
      setSpaceAdjustments((prev) =>
        prev.map((adj) => {
          if (adj.spaceId !== spaceId) return adj;
          const space = selectedSpaces.find((s) => s.id === spaceId);
          if (!space) return adj;
          return {
            spaceId,
            ...calculateSpaceValues(
              getMonthlyPrice(space),
              DEFAULT_SPACE_TAKE_PCT,
              extraValue,
            ),
            setupCost: adj.setupCost,
          };
        }),
      );
    },
    [selectedSpaces],
  );

  const handleNextRef = useRef<() => void>(() => {});
  handleNextRef.current = () => {
    if (licenseFeeCalc.value > 0) {
      sendTrackDataServer({
        eventName: "PROPOSAL_LICENSE_FEE_VALUE_SET",
        productType: "PO",
        url: window.location.pathname,
        data: {
          deal_id: state.diagnosis?.dealId ?? null,
          user_id: userInfo?.user?.id ?? null,
          email: userInfo?.profile?.email ?? null,
          format: licenseFeeMode,
          percentage_value:
            licenseFeeMode === "percentage" ? licenseFeePercentage : null,
          fixed_value:
            licenseFeeMode === "fixed" ? (licenseFeeFixed ?? null) : null,
          calculated_value: licenseFeeCalc.value,
          avg_room_price: licenseFeeCalc.avgPrice,
          rooms_count: spaceAdjustments.length,
        },
      });
    }
    const orderedAdjustments = orderedSpaceIds
      .map((id) => spaceAdjustments.find((a) => a.spaceId === id))
      .filter((a): a is SpaceAdjustment => a != null);

    onNext({
      creditsPrice: FIXED_CREDIT_PRICE,
      proposalType,
      contractMonths,
      spaceAdjustments: orderedAdjustments,
      orderedSpaceIds,
      proposalValidity: new Date(proposalValidity).getTime(),
      sellerNotes,
      recommendedSpaceIds,
      selectedSpaces: orderedSpaces,
      includeFlexCredits,
      flexCredits: includeFlexCredits ? flexCredits || undefined : undefined,
      showLicenseFee,
      licenseFeeMode,
      licenseFeePercentage,
      licenseFeeFixed,
      includeSetupFee,
      setupFee: includeSetupFee ? setupFee || undefined : undefined,
    });
  };

  const licenseFeeIsValid =
    !showLicenseFee ||
    licenseFeeMode === "percentage" ||
    licenseFeeFixed != null;
  // O backend rejeita include_setup_fee=true sem valor (422)
  const setupFeeIsValid = !includeSetupFee || (setupFee !== "" && setupFee > 0);
  const isValid = licenseFeeIsValid && setupFeeIsValid;

  useEffect(() => {
    registerNext?.(() => handleNextRef.current());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    const orderedAdjustments = orderedSpaceIds
      .map((id) => spaceAdjustments.find((a) => a.spaceId === id))
      .filter((a): a is SpaceAdjustment => a != null);

    onChange?.({
      creditsPrice: FIXED_CREDIT_PRICE,
      proposalType,
      contractMonths,
      spaceAdjustments: orderedAdjustments,
      orderedSpaceIds,
      proposalValidity: new Date(proposalValidity).getTime(),
      sellerNotes,
      recommendedSpaceIds,
      selectedSpaces: orderedSpaces,
      includeFlexCredits,
      flexCredits: includeFlexCredits ? flexCredits || undefined : undefined,
      showLicenseFee,
      licenseFeeMode,
      licenseFeePercentage,
      licenseFeeFixed,
      includeSetupFee,
      setupFee: includeSetupFee ? setupFee || undefined : undefined,
    });
  }, [
    proposalType,
    contractMonths,
    spaceAdjustments,
    orderedSpaceIds,
    orderedSpaces,
    proposalValidity,
    sellerNotes,
    recommendedSpaceIds,
    includeFlexCredits,
    flexCredits,
    showLicenseFee,
    licenseFeeMode,
    licenseFeePercentage,
    licenseFeeFixed,
    includeSetupFee,
    setupFee,
    onChange,
  ]);

  return (
    <div className="space-y-6">
      <div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
          style={{
            background: "rgba(255,217,0,0.1)",
            border: "1px solid rgba(255,217,0,0.25)",
            color: "#1A1814",
          }}
        >
          <Settings2 className="w-3.5 h-3.5" />
          Ajustes
        </span>
        <h2
          className="text-2xl font-bold mt-4 mb-1"
          style={{ color: "#1A1814" }}
        >
          Ajustes da Proposta
        </h2>
        <p className="text-sm" style={{ color: "#78736D" }}>
          Configure valores e personalize a proposta
        </p>
        <div className="mt-4 space-y-2">
          <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
            Tipo de proposta
          </Label>
          <div className="flex gap-2">
            {PROPOSAL_TYPE_OPTIONS.map((type) => (
              <button
                key={type}
                type="button"
                aria-pressed={proposalType === type}
                disabled={isEditMode}
                onClick={() => handleProposalTypeChange(type)}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: proposalType === type ? "#FFD900" : "white",
                  border: `1.5px solid ${proposalType === type ? "#FFD900" : "#E8E6E3"}`,
                  color: "#1A1814",
                }}
              >
                {PROPOSAL_TYPE_LABELS[type]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className="rounded-2xl p-5 space-y-4"
        style={{
          background: "#FDFBF7",
          border: "1.5px solid #E8E6E3",
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className="w-5 h-5 rounded flex items-center justify-center"
            style={{ background: "#F0EEEB" }}
          >
            <DollarSign className="w-3 h-3" style={{ color: "#78736D" }} />
          </span>
          <span
            className="text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: "#9E9A94" }}
          >
            Configurações globais
          </span>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
            Valor do crédito (R$)
          </Label>
          <p className="text-sm font-semibold" style={{ color: "#1A1814" }}>
            R$ {FIXED_CREDIT_PRICE},00
          </p>
          <p className="text-[10px]" style={{ color: "#9E9A94" }}>
            Valor fixo definido pela política comercial
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
            Prazo do contrato
          </Label>
          <div className="flex gap-2">
            {CONTRACT_TERM_OPTIONS.map((months) => (
              <button
                key={months}
                type="button"
                onClick={() => setContractMonths(months)}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: contractMonths === months ? "#FFD900" : "white",
                  border: `1.5px solid ${contractMonths === months ? "#FFD900" : "#E8E6E3"}`,
                  color: "#1A1814",
                }}
              >
                {months} meses
              </button>
            ))}
          </div>
        </div>
      </div>

      <Divider label="Salas selecionadas" />

      <div className="space-y-3">
        {orderedItems.map((item, idx) => {
          const canMoveUp = idx > 0;
          const canMoveDown = idx < orderedItems.length - 1;

          const moveItem = (direction: "up" | "down") => {
            setOrderedSpaceIds(() => {
              const blocks = orderedItems.map((it) =>
                it.type === "single" ? [it.spaceId] : it.spaceIds,
              );
              const target = direction === "up" ? idx - 1 : idx + 1;
              [blocks[target], blocks[idx]] = [blocks[idx], blocks[target]];
              // A room that is both individual (single block) and in a combo
              // block would otherwise flatten to a duplicate id — keep unique.
              return Array.from(new Set(blocks.flat()));
            });
          };

          if (item.type === "single") {
            const space = selectedSpaces.find((s) => s.id === item.spaceId);
            const adj = spaceAdjustments.find(
              (a) => a.spaceId === item.spaceId,
            );
            if (!space || !adj) return null;
            return (
              <SpaceAdjustmentCard
                key={space.id}
                space={space}
                adjustment={adj}
                isRecommended={recommendedSpaceIds.includes(space.id)}
                onExtraValueChange={(v) => handleExtraValueChange(space.id, v)}
                onToggleRecommended={() => handleToggleRecommended(space.id)}
                staleStatus={staleStatusBySpaceId.get(space.id)}
                canMoveUp={canMoveUp}
                canMoveDown={canMoveDown}
                onMoveUp={() => moveItem("up")}
                onMoveDown={() => moveItem("down")}
              />
            );
          }

          // Combination item. Pair each room with its adjustment BEFORE
          // filtering so the two never drift out of sync — otherwise a room
          // present in one lookup but not the other would shift indices and
          // render the wrong price on a card.
          const roomPairs = item.spaceIds
            .map((id) => ({
              space: selectedSpaces.find((s) => s.id === id),
              adj: spaceAdjustments.find((a) => a.spaceId === id),
            }))
            .filter(
              (p): p is { space: RankedSpace; adj: SpaceAdjustment } =>
                p.space != null && p.adj != null,
            );

          return (
            <div
              key={item.combinationId}
              className="rounded-xl border"
              style={{
                borderColor: "#FFD900",
                background: "rgba(255,217,0,0.03)",
              }}
            >
              {/* Combo header */}
              <div
                className="flex items-center justify-between gap-2 px-4 py-3 border-b"
                style={{ borderColor: "rgba(255,217,0,0.35)" }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0"
                    style={{ background: "#FFD900", color: "#1A1814" }}
                  >
                    <Link2 className="w-2.5 h-2.5" />
                    Combinação
                  </span>
                  <span
                    className="text-xs truncate"
                    style={{ color: "#78736D" }}
                  >
                    {item.combo.spaceName}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div
                    className="flex items-center gap-3 text-xs"
                    style={{ color: "#43403D" }}
                  >
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" style={{ color: "#9E9A94" }} />
                      <span className="font-medium">
                        {item.combo.totalCapacity}
                      </span>
                      <span style={{ color: "#9E9A94" }}>pos.</span>
                    </span>
                    <span className="font-semibold">
                      R${" "}
                      {roomPairs
                        .reduce((sum, p) => sum + p.adj.totalPrice, 0)
                        .toLocaleString("pt-BR")}
                      /mês
                    </span>
                  </div>
                  {(() => {
                    const comboAllRecommended = item.spaceIds.every((id) =>
                      recommendedSpaceIds.includes(id),
                    );
                    return (
                      <button
                        type="button"
                        onClick={() =>
                          handleToggleComboRecommended(item.spaceIds)
                        }
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all shrink-0"
                        style={
                          comboAllRecommended
                            ? { background: "#F9E10B", color: "#1A1814" }
                            : {
                                border: "1.5px dashed #C8C4BE",
                                color: "#9E9A94",
                                background: "transparent",
                              }
                        }
                      >
                        {comboAllRecommended ? "★ Recomendado" : "☆ Recomendar"}
                      </button>
                    );
                  })()}
                  <TooltipProvider>
                    <div className="flex items-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => moveItem("up")}
                            disabled={!canMoveUp}
                            aria-label="Mover combinação para cima"
                            className="flex items-center justify-center w-6 h-6 rounded transition-colors disabled:opacity-20 hover:bg-yellow-50"
                            style={{ color: "#9E9A94" }}
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Mover combinação para cima</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => moveItem("down")}
                            disabled={!canMoveDown}
                            aria-label="Mover combinação para baixo"
                            className="flex items-center justify-center w-6 h-6 rounded transition-colors disabled:opacity-20 hover:bg-yellow-50"
                            style={{ color: "#9E9A94" }}
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Mover combinação para baixo</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                </div>
              </div>

              {/* Individual room cards — no move buttons (group moves as a unit) */}
              <div className="p-3 space-y-3">
                {roomPairs.map(({ space, adj }) => {
                  return (
                    <SpaceAdjustmentCard
                      key={space.id}
                      space={space}
                      adjustment={adj}
                      isRecommended={false}
                      onExtraValueChange={(v) =>
                        handleExtraValueChange(space.id, v)
                      }
                      onToggleRecommended={() => {}}
                      staleStatus={staleStatusBySpaceId.get(space.id)}
                      hideRecommendedButton
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Divider label="Configurações da proposta" />

      <div className="space-y-2">
        <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
          Validade da proposta
        </Label>
        <Input
          type="date"
          value={proposalValidity}
          onChange={(e) => setProposalValidity(e.target.value)}
          onClick={(e) => e.currentTarget.showPicker?.()}
          className="cursor-pointer"
        />
      </div>

      <div
        className="rounded-xl p-4 space-y-4"
        style={{ border: "1.5px solid #E8E6E3", background: "#FDFBF7" }}
      >
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
            License Fee
          </Label>
          <button
            type="button"
            onClick={() => setShowLicenseFee((v) => !v)}
            style={{
              position: "relative",
              width: "44px",
              height: "24px",
              borderRadius: "12px",
              background: showLicenseFee ? "#FFD900" : "#E8E6E3",
              border: "none",
              cursor: "pointer",
              transition: "background 0.2s",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: "absolute",
                top: "2px",
                left: showLicenseFee ? "22px" : "2px",
                width: "20px",
                height: "20px",
                borderRadius: "10px",
                background: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </button>
        </div>

        {showLicenseFee && (
          <>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleLicenseFeeModeChange("percentage")}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  background:
                    licenseFeeMode === "percentage" ? "#FFD900" : "white",
                  border: `1.5px solid ${licenseFeeMode === "percentage" ? "#FFD900" : "#E8E6E3"}`,
                  color: "#1A1814",
                }}
              >
                Percentual (%)
              </button>
              <button
                type="button"
                onClick={() => handleLicenseFeeModeChange("fixed")}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: licenseFeeMode === "fixed" ? "#FFD900" : "white",
                  border: `1.5px solid ${licenseFeeMode === "fixed" ? "#FFD900" : "#E8E6E3"}`,
                  color: "#1A1814",
                }}
              >
                Valor fixo (R$)
              </button>
            </div>

            {licenseFeeMode === "percentage" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={0.5}
                    value={licenseFeePercentage}
                    onChange={(e) =>
                      setLicenseFeePercentage(Number(e.target.value))
                    }
                    className="flex-1 h-2 rounded-lg cursor-pointer accent-[#FFD900] bg-[#E8E6E3]"
                  />
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={0}
                      max={50}
                      step={0.5}
                      value={licenseFeePercentage}
                      onChange={(e) =>
                        setLicenseFeePercentage(Number(e.target.value) || 0)
                      }
                      className="w-20 text-center"
                    />
                    <span className="text-sm" style={{ color: "#78736D" }}>
                      %
                    </span>
                  </div>
                </div>
                <p className="text-xs" style={{ color: "#9E9A94" }}>
                  Média das salas (R${" "}
                  {licenseFeeCalc.avgPrice.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                  ) x {licenseFeeCalc.multiplier} meses x {licenseFeePercentage}
                  %
                </p>
              </div>
            ) : (
              <Input
                type="number"
                min={0}
                value={licenseFeeFixed ?? ""}
                onChange={(e) =>
                  setLicenseFeeFixed(
                    e.target.value === "" ? undefined : Number(e.target.value),
                  )
                }
                placeholder="Valor em R$"
              />
            )}

            <div
              className="flex justify-between items-center p-3 rounded-lg"
              style={{ background: "#F0EEEB" }}
            >
              <span
                className="text-sm font-medium"
                style={{ color: "#43403D" }}
              >
                Valor final:
              </span>
              <span className="text-lg font-bold" style={{ color: "#1A1814" }}>
                R${" "}
                {licenseFeeCalc.value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <p className="text-xs" style={{ color: "#9E9A94" }}>
              {licenseFeeCalc.period === "semiannual"
                ? "pago semestralmente"
                : "pago anualmente"}
            </p>
          </>
        )}
      </div>

      <div
        className="rounded-xl p-4 space-y-3"
        style={{ border: "1.5px solid #E8E6E3", background: "#FDFBF7" }}
      >
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
            Incluir Setup Fee na proposta
          </Label>
          <button
            type="button"
            onClick={() => setIncludeSetupFee((v) => !v)}
            style={{
              position: "relative",
              width: "44px",
              height: "24px",
              borderRadius: "12px",
              background: includeSetupFee ? "#FFD900" : "#E8E6E3",
              border: "none",
              cursor: "pointer",
              transition: "background 0.2s",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: "absolute",
                top: "2px",
                left: includeSetupFee ? "22px" : "2px",
                width: "20px",
                height: "20px",
                borderRadius: "10px",
                background: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </button>
        </div>
        {includeSetupFee && (
          <div className="space-y-1">
            <Label className="text-xs font-medium" style={{ color: "#78736D" }}>
              Valor do Setup Fee
            </Label>
            <Input
              type="number"
              min={0}
              value={setupFee}
              onChange={(e) =>
                setSetupFee(e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="Valor em R$"
            />
            <p className="text-xs" style={{ color: "#9E9A94" }}>
              pago uma única vez
            </p>
          </div>
        )}
      </div>

      <div
        className="rounded-xl p-4 space-y-3"
        style={{ border: "1.5px solid #E8E6E3", background: "#FDFBF7" }}
      >
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
            Incluir créditos flexíveis na proposta
          </Label>
          <button
            type="button"
            onClick={() => setIncludeFlexCredits((v) => !v)}
            style={{
              position: "relative",
              width: "44px",
              height: "24px",
              borderRadius: "12px",
              background: includeFlexCredits ? "#FFD900" : "#E8E6E3",
              border: "none",
              cursor: "pointer",
              transition: "background 0.2s",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: "absolute",
                top: "2px",
                left: includeFlexCredits ? "22px" : "2px",
                width: "20px",
                height: "20px",
                borderRadius: "10px",
                background: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s",
              }}
            />
          </button>
        </div>
        {includeFlexCredits && (
          <div className="space-y-1">
            <Label className="text-xs font-medium" style={{ color: "#78736D" }}>
              Quantidade de créditos flexíveis
            </Label>
            <Input
              type="number"
              min={0}
              value={flexCredits}
              onChange={(e) =>
                setFlexCredits(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              placeholder="0"
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium" style={{ color: "#43403D" }}>
          Observações do vendedor
        </Label>
        <textarea
          rows={4}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
          style={{ color: "#1A1814" }}
          placeholder="Notas internas da proposta..."
          value={sellerNotes}
          onChange={(e) => setSellerNotes(e.target.value)}
        />
      </div>
    </div>
  );
}
